'use client'

import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { SongArtwork } from '@/components/ui/song-artwork'
import {
  Search,
  ThumbsUp,
  DollarSign,
  X,
  Info,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { InlineSheet } from './inline-sheet'
import { MockPaymentSheet } from '../mock-payment-sheet'
import type { DemoSong, DemoQueueRequest, DemoEventSettings } from '../demo-types'

interface RequestSheetProps {
  open: boolean
  onClose: () => void
  songs: DemoSong[]
  queue: DemoQueueRequest[]
  settings: DemoEventSettings
  nowPlayingSongId: string | null
  squelchedSongIds: Set<string>
  visitorUpvotedRequestIds: Set<string>
  artistName: string
  onRequestSong: (song: DemoSong, tipCents: number) => void
  onUpvote: (request: DemoQueueRequest) => void
  onOpenBoost: (request: DemoQueueRequest) => void
}

export function RequestSheet({
  open,
  onClose,
  songs,
  queue,
  settings,
  nowPlayingSongId,
  squelchedSongIds,
  visitorUpvotedRequestIds,
  artistName,
  onRequestSong,
  onUpvote,
  onOpenBoost,
}: RequestSheetProps) {
  const t = useTranslations('demo')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSong, setSelectedSong] = useState<DemoSong | null>(null)
  const [selectedTipAmount, setSelectedTipAmount] = useState(500)
  const [showPayment, setShowPayment] = useState(false)

  // Filter songs based on search
  const filteredSongs = useMemo(() => {
    const available = songs.filter(s => !squelchedSongIds.has(s.id))
    if (!searchQuery.trim()) return available.slice(0, 50)
    const query = searchQuery.toLowerCase()
    return available.filter(
      s => s.title.toLowerCase().includes(query) || s.artist.toLowerCase().includes(query)
    ).slice(0, 50)
  }, [songs, searchQuery, squelchedSongIds])

  const handleClose = () => {
    setSelectedSong(null)
    setShowPayment(false)
    setSearchQuery('')
    onClose()
  }

  const handleRequestSong = (song: DemoSong, tipCents: number) => {
    onRequestSong(song, tipCents)
    setSelectedSong(null)
    setShowPayment(false)
    setSearchQuery('')
  }

  const isSongInQueue = (songId: string) => queue.some(r => r.songId === songId)
  const isSongPlaying = (songId: string) => nowPlayingSongId === songId

  return (
    <InlineSheet
      open={open}
      onClose={handleClose}
      title={selectedSong ? t('requestSheetTitle', { songTitle: selectedSong.title }) : t('requestASong')}
      description={selectedSong
        ? (settings.tipsEnabled ? t('addTipToBoost') : t('submitYourRequest'))
        : t('browseSongs', { artistName })
      }
    >
      {!selectedSong ? (
        <div className="p-4 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              placeholder={t('searchSongs')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-zinc-800 border-zinc-700 text-white"
              autoFocus
            />
          </div>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {filteredSongs.map((song, index) => {
              const inQueue = isSongInQueue(song.id)
              const playing = isSongPlaying(song.id)
              const isTopPick = index < 3 && !inQueue && !playing
              const queueItem = queue.find(r => r.songId === song.id)
              const hasTips = queueItem && queueItem.totalTipsCents > 0

              return (
                <div
                  key={song.id}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                    hasTips
                      ? 'bg-gradient-to-r from-emerald-500/15 to-green-500/10 border border-emerald-500/30 hover:border-emerald-500/50'
                      : inQueue
                      ? 'bg-violet-500/10 border border-violet-500/30'
                      : isTopPick
                      ? 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 hover:border-amber-500/40'
                      : playing
                      ? 'bg-violet-500/20 border border-violet-500/40'
                      : 'bg-zinc-800/50 hover:bg-zinc-800'
                  }`}
                  onClick={() => {
                    if (!inQueue && !playing) {
                      setSelectedSong(song)
                    }
                  }}
                >
                  <div className="relative">
                    <SongArtwork
                      src={song.artworkUrl}
                      alt={song.title}
                      size={48}
                      className="h-12 w-12 rounded-md"
                    />
                    {hasTips && (
                      <div className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[8px] font-bold px-1 rounded">
                        ${(queueItem.totalTipsCents / 100).toFixed(0)}
                      </div>
                    )}
                    {!hasTips && isTopPick && (
                      <div className="absolute -top-1 -right-1 bg-amber-500 text-black text-[10px] font-bold px-1 rounded">
                        {t('top')}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate text-white">{song.title}</p>
                    </div>
                    <p className="text-sm truncate text-zinc-400">{song.artist}</p>
                    {inQueue && (
                      <p className="text-xs text-violet-400 mt-0.5">{t('inQueueUpvoteOrBoost')}</p>
                    )}
                    {playing && (
                      <p className="text-xs text-violet-400 mt-0.5">{t('nowPlayingLabel')}</p>
                    )}
                  </div>
                  {inQueue ? (
                    <div className="flex items-center gap-2">
                      {!visitorUpvotedRequestIds.has(queueItem!.id) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-500/30 text-blue-400 bg-blue-500/10 hover:bg-blue-500/20"
                          onClick={(e) => {
                            e.stopPropagation()
                            onUpvote(queueItem!)
                            handleClose()
                          }}
                        >
                          <ThumbsUp className="h-3 w-3 mr-1" />
                          {t('upvote')}
                        </Button>
                      )}
                      {settings.tipsEnabled && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-500/30 text-green-400 bg-green-500/10 hover:bg-green-500/20"
                          onClick={(e) => {
                            e.stopPropagation()
                            onOpenBoost(queueItem!)
                            handleClose()
                          }}
                        >
                          <DollarSign className="h-3 w-3 mr-1" />
                          {t('boost')}
                        </Button>
                      )}
                    </div>
                  ) : playing ? (
                    <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30">
                      {t('playing')}
                    </Badge>
                  ) : isTopPick ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-500/50 text-amber-400 bg-amber-500/10"
                    >
                      {t('select')}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-violet-500/30 text-violet-400"
                    >
                      {t('select')}
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ) : showPayment ? (
        <MockPaymentSheet
          amount={selectedTipAmount}
          onSuccess={() => handleRequestSong(selectedSong, selectedTipAmount)}
          onCancel={() => setShowPayment(false)}
          description={t('requestBoostDescription', { songTitle: selectedSong.title, amount: (selectedTipAmount / 100).toFixed(0) })}
          inline
        />
      ) : (
        <div className="p-4 space-y-4">
          {/* Selected song */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50">
            <SongArtwork
              src={selectedSong.artworkUrl}
              alt={selectedSong.title}
              size={48}
              className="h-12 w-12 rounded-md"
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-white truncate">{selectedSong.title}</p>
              <p className="text-sm text-zinc-400 truncate">{selectedSong.artist}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Deselect song"
              onClick={() => setSelectedSong(null)}
              className="shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Request without tip */}
          <Button
            variant="outline"
            className="w-full border-zinc-700 hover:bg-zinc-800 h-12"
            onClick={() => handleRequestSong(selectedSong, 0)}
          >
            {t('requestWithoutTip')}
          </Button>

          {settings.tipsEnabled && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-zinc-700" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-zinc-900 px-2 text-zinc-500">{t('addTipToBoostDivider')}</span>
                </div>
              </div>

              {/* Conditional tip notice */}
              <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <Info className="h-4 w-4 text-amber-400 shrink-0" />
                <p className="text-xs text-amber-300">
                  {t('onlyChargedIfPlayed')}
                </p>
              </div>

              {/* Tip amount selection */}
              <div className="grid grid-cols-4 gap-2">
                {settings.tipAmounts.map(amount => (
                  <Button
                    key={amount}
                    variant={selectedTipAmount === amount ? 'default' : 'outline'}
                    className={selectedTipAmount === amount
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 border-0'
                      : 'border-zinc-700'}
                    onClick={() => setSelectedTipAmount(amount)}
                  >
                    ${(amount / 100).toFixed(0)}
                  </Button>
                ))}
              </div>

              <Button
                className="w-full h-12 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                onClick={() => {
                  setShowPayment(true)
                }}
              >
                <DollarSign className="h-4 w-4 mr-2" />
                {t('requestWithTip', { amount: (selectedTipAmount / 100).toFixed(0) })}
              </Button>
            </>
          )}
        </div>
      )}
    </InlineSheet>
  )
}
